import { Box, Button, Grid, IconButton, Stack, Typography } from "@mui/material";
import CustomerInformation from './CustomerInformation';
import DeliveryInformation from './DeliveryInformation';
import destroy from '../../../../api/public/storefronts/reservations/destroy';
import show from '../../../../api/auth/orders/show';
import AdditionalInformation from "./AdditionalInformation";
import { CheckCircle, Place } from "@mui/icons-material";
import useCheckoutStore from "../../../../stores/public/useCheckoutStore";
import { useMemo, useRef, useState } from "react";
import OrderSummary from "../../storePages/reservation/checkout/components/OrderSummary";
import WushCard from "../../../layout/WushCard";
import OrderCreditCardCheckout from "../../orders/OrderCreditCardCheckout";
import useStorefrontStore from "../../../../stores/public/useStorefrontStore";

const StepNumber = ({ number }) => (
  <Stack sx={{ my: 'auto !important', bgcolor: "darkgrey", borderRadius: '100px', width: "25px", height: "25px"}}><Typography sx={{ m: 'auto !important', color: "#ffffff" }}>{number}</Typography></Stack>
);

function formatPhoneNumber(phoneNumberString) {
  var cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    var intlCode = (match[1] ? '+1 ' : '');
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }
  return null;
}

export default function CheckoutWizard({ reservations }) {
  const checkoutRef = useRef(null);
  const [additionalQuestionsAnswered, setAdditionalQuestionsAnswered] = useState(false);
  const [deliveryInfoComplete, setDeliveryInfoComplete] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  const { organization } = useStorefrontStore();
  const { customer, location, order, setOrder, removeReservation } = useCheckoutStore();

  const deliveryInfoRequired = reservations.filter(reservation => reservation?.reservation_items[0]?.item.item_category?.deliverable).length > 0;
  const customerCanPickup = reservations.filter(reservation => reservation?.reservation_items[0]?.item.item_category?.customer_pickupable).length > 0;

  const handleRemoveReservation = (reservation) => {
    removeReservation(reservation);

    destroy(organization.id, reservation.id, () => {
      refreshOrder();
    });
  };

  const refreshOrder = () => {
    show(order.id, (response) => {
      setOrder(response);
    });
  };

  const readyForCheckout = useMemo(() => {
    return order.customer_id && (deliveryInfoRequired ? (order.location_id || deliveryInfoComplete) : true);
  }, [order, deliveryInfoRequired, deliveryInfoComplete]);

  const reservationAddressString = () => {
    return `${organization.address_1} ${organization.city} ${organization.state} ${organization.zip}`;
  };

  const handleGoogleMaps = () => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${reservationAddressString()}`);
  };

  if(!reservations || reservations.length === 0) return <Stack>
    <Box>
      <h1>Your cart is empty</h1>
    </Box>
  </Stack>;

  return (
    <Stack mb={25}>
      <Stack px={{ sm: "10%" }} spacing={4}>
        <Grid container spacing={{ xs: 0, sm: 2 }}>
          <Grid item xs={12} sm={8}>
            <Stack mb={2} spacing={1}>
              <Typography variant="h4" fontWeight="bold">Checkout</Typography>
              {organization.address_1 && <Stack direction="row">
                <IconButton onClick={handleGoogleMaps}>
                  <Place />
                </IconButton>
                <Typography variant="h5" fontWeight="bold" sx={{ my: 'auto !important'}}>
                  {organization.name}
                </Typography>
              </Stack>}
              {organization.support_phone && <Typography variant="h5">Need help? Call us at {formatPhoneNumber(organization.support_phone)}</Typography>}
            </Stack>
            <Stack spacing={4}>
              <Stack>
                <Stack direction="row" spacing={1} mb={1}>
                  {customer?.id ? <CheckCircle fontSize="large" sx={{ my: 'auto !important', fill: "green" }}/> : <StepNumber number={1} />}
                  <Typography variant="h4">Customer Information</Typography>
                </Stack>
                <CustomerInformation reservations={reservations} />
              </Stack>
              {
                customer?.id && <Stack spacing={4}>
                  {deliveryInfoRequired && <Stack>
                    <Stack direction="row" spacing={1} mb={1}>
                    {deliveryInfoComplete || location?.id ? <CheckCircle fontSize="large" sx={{ my: 'auto !important', fill: "green" }}/> :
                      <StepNumber number={2} />
                    }
                      <Typography variant="h4">Delivery Information</Typography>
                    </Stack>
                    <DeliveryInformation customerCanPickup={customerCanPickup} previousStep={() => setDeliveryInfoComplete(false)} nextStep={() => setDeliveryInfoComplete(true)}/>
                  </Stack>}
                  <Stack>
                  <Stack direction="row" spacing={1} mb={1}>
                    {additionalQuestionsAnswered ? <CheckCircle fontSize="large" sx={{ my: 'auto !important', fill: "green" }}/> :
                      <StepNumber number={3} />
                    }
                      <Typography variant="h4">Additional Information</Typography>
                    </Stack>
                    <AdditionalInformation reservations={reservations} nextStep={() => {
                      setAdditionalQuestionsAnswered(true);
                      if(readyForCheckout) setCheckingOut(true);
                      checkoutRef.current.scrollIntoView();
                    }}/>
                  </Stack>
                  <Stack ref={checkoutRef}>
                    {checkingOut ?
                      <Stack mb={2}>
                        <WushCard bgcolor={"#ffffff"}>
                          <Stack p={2}>
                            <OrderCreditCardCheckout
                              order={order}
                              returnUrl={`https://ims.wush.life/storefront/${order.organization_id}/order_confirmation`}
                              afterSuccess={refreshOrder}
                            />
                          </Stack>
                        </WushCard>
                      </Stack>
                      :
                      <Stack width="100%" justifyContent="center" px={"25%"} mb={2}>
                        <Button disabled={!readyForCheckout} variant="contained" onClick={() => setCheckingOut(true)}>Checkout</Button>
                      </Stack>}
                  </Stack>
                </Stack>
              }

            </Stack>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Stack position="sticky" top={"25px"}>
              <WushCard bgcolor={"#ffffff"}>
                <Stack p={2}>
                  <OrderSummary order={order} reservations={reservations} showCheckoutButton={false} handleRemoveReservation={handleRemoveReservation} />
                </Stack>
              </WushCard>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Stack>
  );
}

export { formatPhoneNumber };